import { Account, UserType } from '@/types';

/**
 * Authentication method types for user display
 */
export type AuthMethod = 'sso' | 'local';

/**
 * Strips internal agent tags (e.g., "[cato-agent]") from the beginning of comment text.
 * Only strips tokens whose content ends with "-agent" or is exactly "agent".
 * Multiple consecutive leading agent tags are all stripped.
 * Only applied for non-Praetorian viewers.
 */
export function stripAgentTags(
  text: string | null | undefined,
  userType: UserType
): string {
  if (text == null) return '';
  if (userType === 'praetorian') return text;
  return text.replace(/^(?:\[[\w-]*agent\]\s*)+/gi, '');
}

/**
 * Determines if an email belongs to a Praetorian employee
 */
export function isPraetorianEmail(email: string): boolean {
  // Defensive check: handle null/undefined/non-string inputs
  if (!email || typeof email !== 'string') {
    return false;
  }

  return (
    email.endsWith('@praetorian.com') || email.endsWith('@praetorianlabs.email')
  );
}

/**
 * Masks Praetorian employee names for customer viewing
 * Praetorian users see full names, customers see "The Praetorian Guard"
 */
export function maskPraetorianName(
  email: string,
  displayName: string | undefined,
  userType: UserType
): string {
  // If viewer is Praetorian, show real name or email
  if (userType === 'praetorian') {
    return displayName || email;
  }

  // If author is Praetorian and viewer is customer, mask the name
  if (isPraetorianEmail(email)) {
    return 'The Praetorian Guard';
  }

  // Show customer names/emails normally
  return displayName || email;
}

/**
 * Extracts configured SSO domains from SSO account entries
 * SSO accounts have member field starting with 'sso:' (e.g., 'sso:example.com')
 */
export function getSSODomains(accounts: Account[]): string[] {
  if (!accounts || !Array.isArray(accounts)) {
    return [];
  }

  return accounts
    .filter(account => account.member?.startsWith('sso:'))
    .map(account => account.member.split(':')[1])
    .filter(Boolean);
}

/**
 * Determines the authentication method for a user based on their email
 * and the configured SSO domains for the account
 *
 * @param userEmail - The email address of the user
 * @param ssoDomains - List of configured SSO domains for the account
 * @returns 'sso' if user's domain matches an SSO domain, 'local' otherwise
 */
export function getAuthMethod(
  userEmail: string,
  ssoDomains: string[]
): AuthMethod {
  // Defensive check
  if (!userEmail || typeof userEmail !== 'string') {
    return 'local';
  }

  // Praetorian users always use local auth (Google SSO for internal, not customer SSO)
  if (isPraetorianEmail(userEmail)) {
    return 'local';
  }

  // No SSO configured means local auth
  if (!ssoDomains || ssoDomains.length === 0) {
    return 'local';
  }

  // Extract domain from email
  const emailDomain = userEmail.split('@')[1]?.toLowerCase();
  if (!emailDomain) {
    return 'local';
  }

  // Check if user's email domain matches any configured SSO domain
  const isSSO = ssoDomains.some(
    ssoDomain => ssoDomain.toLowerCase() === emailDomain
  );

  return isSSO ? 'sso' : 'local';
}
